import {ChangeDetectorRef, Component, OnInit, Input, Output, EventEmitter} from '@angular/core';
import {NestedTreeControl} from '@angular/cdk/tree';
import {MatTreeNestedDataSource} from '@angular/material/tree';
import {faFolder, faFolderOpen} from "@fortawesome/free-solid-svg-icons";
import {Compbaser} from '../../../components/compbaser';
import {traverseTree} from '../../../common';
import {getFontIcons} from '../../../common/get-icons';
import {IEventBlock} from '../../../interfaces';
import * as _ from "lodash";

interface TreeData {
    name: string;
    sceneId: string;
    blockId: string;
    children?: TreeData[];
    icon?: any;
}

@Component({
    selector: 'app-events-tree',
    templateUrl: './events-tree.component.html',
    styleUrls: ['./events-tree.component.scss']
})
export class EventsTreeComponent extends Compbaser implements OnInit {
    readonly faFolder = faFolder;
    readonly faFolderOpen = faFolderOpen;

    treeControl = new NestedTreeControl<TreeData>(node => node.children);
    dataSource = new MatTreeNestedDataSource<TreeData>();

    eventBlocks: IEventBlock[] = [];
    searchedEvents: TreeData[] = [];
    filter = "";
    activeNode: TreeData;

    @Input()
    set setEventBlocks(i_value) {
        if (i_value) {
            this.eventBlocks = i_value;
        } else {
            this.eventBlocks = [];
        }
        this.cd.markForCheck();
        this.buildTreeData(this.eventBlocks);
    }

    @Input() selectedEvent: IEventBlock;

    @Output() updateFilter: EventEmitter<any> = new EventEmitter();

    constructor(public cd: ChangeDetectorRef) {
        super();
    }

    ngOnInit(): void {
        this.buildTreeData(this.eventBlocks);
        this.activeNode = {
            name: '',
            sceneId: this.selectedEvent?.eventSceneId,
            blockId: this.selectedEvent?.eventBlockId
        };
        this.cd.detectChanges();
    }

    hasChild = (_: number, node: TreeData) => !!node.children && node.children.length > 0;

    /**
     * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
     * The return value is the list of `TreeData`.
     */
    private buildTreeData(items: IEventBlock[]) {
        if (!this.dataSource || !items) return;

        const sceneEventNodeList: Array<TreeData> = [];
        const eventDatabaseNodeList: Array<TreeData> = [];

        items.forEach((item) => {
            let nodeList: Array<TreeData> = sceneEventNodeList;
            if (item.eventSceneId === 'none') {
                nodeList = eventDatabaseNodeList;
            }
            const node = nodeList.find((_node) => _node.sceneId === item.eventSceneId);

            if (node) {
                if (node.children.find((_node) => _node.blockId === item.eventBlockId)) {
                    return;
                }
                node.children.push({
                    name: item.blockName,
                    sceneId: item.eventSceneId,
                    blockId: item.eventBlockId,
                    icon: this.getIcon(item),
                    children: []
                });
            } else {
                nodeList.push({
                    name: item.sceneName,
                    sceneId: item.eventSceneId,
                    blockId: '',
                    icon: getFontIcons({$: {id: '3510'}}).icon,
                    children: [{
                        name: item.blockName,
                        sceneId: item.eventSceneId,
                        blockId: item.eventBlockId,
                        icon: this.getIcon(item),
                        children: []
                    }]
                });
            }
        });

        this.dataSource.data = [{
            name: 'All Events',
            sceneId: '',
            blockId: '',
            icon: faFolder,
            children: [
                ...eventDatabaseNodeList,
                {
                    name: 'events of scenes',
                    sceneId: '-1',
                    blockId: '',
                    icon: faFolder,
                    children: sceneEventNodeList
                }
            ]
        }];

        // expand tree
        let expanded = false;
        if (this.selectedEvent) {
            expanded = this.expandNode(this.dataSource.data[0], this.selectedEvent.eventBlockId);
        }
        if (!expanded) {
            this.treeControl.expand(this.dataSource.data[0]);
        }

        this.activeNode = this.dataSource.data[0];
        this.cd.detectChanges();
    }

    private expandNode(data: TreeData, blockId: string): boolean {
        if (data.blockId === blockId) {
            return true;
        }
        if (!data.children || !data.children.length) {
            return false;
        }
        for (const node of data.children) {
            if (this.expandNode(node, blockId)) {
                this.treeControl.expand(data);
                return true;
            }
        }
        return false;
    }

    search() {
        if (this.filter.length < 2 || !this.dataSource) return;
        this.searchedEvents = [];

        traverseTree(this.dataSource.data, (x, y, n) => {
            if (n.sceneId && n.name &&
                n.name.toLowerCase().indexOf(this.filter.toLowerCase()) > -1 &&
                this.searchedEvents.findIndex((v) => v.sceneId === n.sceneId) === -1) {
                this.searchedEvents.push(n);
            }
        });
    }

    clickNode(node: TreeData) {
        if (!node) return;
        this.activeNode = node;
        
        this.updateFilter.emit({
            'eventSceneId': node.sceneId === '-1' ? '(NOT)none' : node.sceneId,
            'eventBlockId': node.blockId || ''
        });
    }

    expandAll() {
        this.treeControl.dataNodes = this.dataSource.data;
        this.treeControl.expandAll();
    }

    collapsAll() {
        this.treeControl.dataNodes = this.dataSource.data;
        this.treeControl.collapseAll();
    }

    private getIcon(item) {
        switch (item.eventType){
            case 'param object': {
                return getFontIcons({$: {id: 'param object'}}).icon;
            }
            case 'param list': {
                return getFontIcons({$: {id: 'param list'}}).icon;
            }
            default: {
                return getFontIcons({$: {id: item.eventBlockPlayer}}).icon;
            }
        }
    }
}
